import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeValeur, TAILLE_TEXTE, COULEUR_TEXTE } from './echartsTheme';
import { densite, resume, estDegenere, formatNombre } from '../utils/eau3mois';
import { COL_TEAL, COL_MED } from './Eau3MoisBandesChart';

/**
 * Distribution du cumul à une date : densité (noyau gaussien) + une marque par
 * trajectoire (rug), minimum/maximum en pointillés, médiane en rouge, moyenne en
 * gris. Les échelles X et Y viennent de `bornes` (calculées une fois par
 * scénario) : le cadre ne bouge pas quand la date change.
 *
 * @param valeurs  cumul de chaque trajectoire à la date choisie (Mm³)
 * @param bornes   { xlim, ylim, bw, n } — cf. bornesDistribution
 */
export default function Eau3MoisDistributionChart({ valeurs, bornes }) {
  const r = resume(valeurs);
  const degenere = estDegenere(r);
  const dens = degenere ? null : densite(valeurs, bornes.bw, bornes.xlim[0], bornes.xlim[1], bornes.n);

  const trait = (x, couleur, type, largeur = 1.5) => ({
    xAxis: x, lineStyle: { color: couleur, type, width: largeur }, label: { show: false },
  });
  const repères = degenere
    ? [trait(valeurs[0], COL_TEAL, 'solid', 3)]
    : [trait(r.min, COL_TEAL, 'dotted'), trait(r.max, COL_TEAL, 'dotted'),
       trait(r.med, COL_MED, 'dashed', 2.5), trait(r.moy, '#404040', 'dotted', 2)];

  const f = (v) => formatNombre(v, 1);
  const texte = [`médiane : ${f(r.med)}`, `moyenne : ${f(r.moy)}`, `min – max : ${f(r.min)} – ${f(r.max)}`,
    `n = ${r.n} trajectoires`].join('\n');

  const option = {
    ...baseOption(),
    grid: { left: 12, right: 28, bottom: 60, top: 56, containLabel: true },
    legend: { show: false },
    tooltip: { show: false },
    graphic: [{
      type: 'text', right: 36, top: 62, z: 10,
      style: { text: texte, fill: '#333', fontSize: TAILLE_TEXTE, lineHeight: 22, textAlign: 'right' },
    }],
    xAxis: {
      type: 'value', min: bornes.xlim[0], max: bornes.xlim[1],
      name: 'Volume cumulé (millions de m³)', nameLocation: 'middle', nameGap: 36,
      nameTextStyle: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE },
      axisLabel: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE, formatter: (v) => formatNombre(v, 0) },
      axisLine: { lineStyle: { color: COULEUR_TEXTE } }, splitLine: { show: false },
    },
    yAxis: { ...axeValeur({ nom: 'Densité', min: 0, max: bornes.ylim[1] }),
      axisLabel: { fontSize: TAILLE_TEXTE, color: COULEUR_TEXTE, formatter: (v) => Number(v).toPrecision(2) } },
    series: [
      {
        type: 'line', symbol: 'none', silent: true,
        lineStyle: { color: '#fff', width: 1 }, areaStyle: { color: COL_TEAL, opacity: 0.85 },
        data: dens ? dens.xs.map((x, i) => [x, dens.ys[i]]) : [],
        // La courbe garde son animation (elle glisse d'une date à l'autre) ; les
        // repères verticaux, eux, doivent apparaître instantanément.
        markLine: { silent: true, symbol: 'none', animation: false, data: repères },
      },
      {
        type: 'scatter', symbol: 'rect', symbolSize: [1.5, 16], symbolOffset: [0, -8], silent: true, animation: false,
        itemStyle: { color: COL_TEAL, opacity: 0.5 }, data: valeurs.map((v) => [v, 0]),
      },
    ],
  };

  return <ResponsiveECharts option={option} style={{ height: 560 }} />;
}
